/**
 * Limits on features a seller already has. Missing sellerAccess, or
 * mode "full", means every current seller feature stays on.
 *
 *   sellerAccess: { mode: "full" | "custom", permissions: string[] }
 *
 * These ids never grant admin surfaces. They only turn seller features off.
 */

import { isSellerAccountUser } from "./employees";

export const SELLER_PERMISSION_TREE = [
  {
    id: "stores",
    label: "Stores",
    children: [
      { id: "stores/create", label: "Create stores" },
      { id: "stores/edit", label: "Edit stores" },
    ],
  },
  {
    id: "products",
    label: "Products",
    children: [
      { id: "products/create", label: "Create products" },
      { id: "products/edit", label: "Edit products" },
      { id: "products/design-library", label: "Design library" },
      { id: "products/listings", label: "Listings" },
    ],
  },
  {
    id: "orders",
    label: "Orders",
    children: [
      { id: "orders/view", label: "View orders" },
      { id: "orders/create", label: "Create orders" },
      { id: "orders/quote-requests", label: "Quote requests" },
    ],
  },
  {
    id: "inventory",
    label: "Inventory",
    children: [
      { id: "inventory/view", label: "View inventory" },
      { id: "inventory/send-stock", label: "Send stock" },
    ],
  },
  {
    id: "account",
    label: "Account",
    children: [
      { id: "account/billing", label: "Billing and payouts" },
      { id: "account/api-keys", label: "API keys" },
      { id: "account/share-access", label: "Share access" },
      { id: "account/certificates", label: "Certificates" },
    ],
  },
];

export function getSellerPermissionIds(tree = SELLER_PERMISSION_TREE) {
  const ids = [];
  (tree || []).forEach((group) => {
    (group.children || []).forEach((child) => {
      if (child?.id) ids.push(child.id);
    });
  });
  return ids;
}

const ALL_SELLER_PERMISSION_IDS = getSellerPermissionIds();

/** Nav / settings ids this limit controls. Value is any-of permission ids. */
const SELLER_VISIBILITY_PERMS = {
  shops: ["stores/create", "stores/edit"],
  catalog: ["products/create"],
  products: ["products/edit"],
  "design-library": ["products/design-library"],
  listings: ["products/listings"],
  "orders-flat/orders": ["orders/view"],
  "orders-flat/create": ["orders/create"],
  "orders-flat/quote-requests": ["orders/quote-requests"],
  "inventory-seller/my-stock": ["inventory/view"],
  "inventory-seller/shipments": ["inventory/view"],
  "inventory-seller/send": ["inventory/send-stock"],
  "inventory-sync-settings": ["inventory/view"],
  certificates: ["account/certificates"],
  "team-access/share-access": ["account/share-access"],
  "api-keys": ["account/api-keys"],
  "billing/billing": ["account/billing"],
  "billing/payments": ["account/billing"],
  "billing/wallet": ["account/billing"],
};

export function isSellerPermissionRestricted(user) {
  if (!isSellerAccountUser(user)) return false;
  if (user.isEnterpriseAdmin) return false;
  return user.sellerAccess?.mode === "custom";
}

export function hasSellerPermission(user, permissionId) {
  if (!isSellerPermissionRestricted(user)) return true;
  const perms = Array.isArray(user.sellerAccess?.permissions)
    ? user.sellerAccess.permissions
    : [];
  return perms.includes(permissionId);
}

export function sellerPermissionSelection(sellerAccess) {
  if (!sellerAccess || sellerAccess.mode !== "custom") {
    return [...ALL_SELLER_PERMISSION_IDS];
  }
  const allowed = new Set(ALL_SELLER_PERMISSION_IDS);
  return (sellerAccess.permissions || []).filter((id) => allowed.has(id));
}

export function normalizeSellerAccess(permissionIds) {
  const allowed = new Set(ALL_SELLER_PERMISSION_IDS);
  const unique = [
    ...new Set((permissionIds || []).filter((id) => allowed.has(id))),
  ];
  if (unique.length === ALL_SELLER_PERMISSION_IDS.length) {
    return { mode: "full", permissions: [] };
  }
  return { mode: "custom", permissions: unique };
}

export function applySellerPermissionVisibility(user, visibility) {
  if (!isSellerPermissionRestricted(user) || !visibility) return visibility;
  const next = { ...visibility };
  Object.entries(SELLER_VISIBILITY_PERMS).forEach(([navId, permIds]) => {
    if (next[navId] === false) return;
    if (!permIds.some((id) => hasSellerPermission(user, id))) {
      next[navId] = false;
    }
  });
  return next;
}

const PATH_RULES = [
  {
    test: (p) => p === "/add-shop" || p.startsWith("/add-shop/"),
    any: ["stores/create"],
  },
  {
    test: (p) => p === "/shops" || p.startsWith("/shops/"),
    any: ["stores/create", "stores/edit"],
  },
  {
    test: (p) =>
      p === "/shop/settings" ||
      p.startsWith("/shop/settings/") ||
      p.startsWith("/stores/"),
    any: ["stores/edit"],
  },
  {
    test: (p) => p.startsWith("/products/new-product"),
    any: ["products/create"],
  },
  {
    test: (p) =>
      p === "/products" ||
      (p.startsWith("/products/") && !p.startsWith("/products/blanks")),
    any: ["products/edit"],
  },
  {
    test: (p) => p.startsWith("/design-library"),
    any: ["products/design-library"],
  },
  {
    test: (p) => p.startsWith("/listings"),
    any: ["products/listings"],
  },
  {
    test: (p) => p.startsWith("/manual-orders"),
    any: ["orders/create"],
  },
  {
    test: (p) =>
      p.startsWith("/request-quote") || p.startsWith("/quote-requests"),
    any: ["orders/quote-requests"],
  },
  {
    test: (p) => p === "/orders" || p.startsWith("/orders/"),
    any: ["orders/view"],
  },
  {
    test: (p) => p.startsWith("/inventory/send-stock"),
    any: ["inventory/send-stock"],
  },
  {
    test: (p) => p === "/inventory" || p.startsWith("/inventory/"),
    any: ["inventory/view"],
  },
  {
    test: (p) => p === "/wallet" || p.startsWith("/wallet"),
    any: ["account/billing"],
  },
  {
    test: (p) => p.startsWith("/settings/certificates"),
    any: ["account/certificates"],
  },
  {
    test: (p) =>
      p === "/settings/team-access" ||
      p.startsWith("/settings/team-access/share-access"),
    any: ["account/share-access"],
  },
  {
    test: (p) => p.startsWith("/settings/api-keys"),
    any: ["account/api-keys"],
  },
  {
    test: (p) =>
      p.startsWith("/settings/billing/billing") ||
      p.startsWith("/settings/billing/payments") ||
      p.startsWith("/settings/billing/wallet"),
    any: ["account/billing"],
  },
];

/** Direct URL check. Unrestricted sellers and admins always pass. */
export function sellerPathAllowed(user, pathname) {
  if (!isSellerPermissionRestricted(user)) return true;
  const path = pathname || "/";
  const rule = PATH_RULES.find((entry) => entry.test(path));
  if (!rule) return true;
  return rule.any.some((id) => hasSellerPermission(user, id));
}
